const cvProjects = [
  {
    "id": "rallyvision",
    "chapter": "research",
    "title": "RallyVision",
    "label": "Temporal tiny-object tracking",
    "period": "2026 · Research system",
    "description": "A five-frame heatmap tracker for a table-tennis ball in 120 FPS monocular video, followed by subpixel decoding, conservative temporal cleanup, and planar bounce mapping.",
    "tech": "Python · PyTorch · OpenCV · temporal heatmaps · homography",
    "metrics": [
      {
        "value": "3.41 px",
        "label": "held-out median error"
      },
      {
        "value": "95.4%",
        "label": "positive frames within 8 px"
      },
      {
        "value": "0.55%",
        "label": "median table-diagonal bounce error"
      }
    ],
    "note": "Development-validation result on held-out game_5. The official OpenTTGames test videos remain untouched, and the presence head still fails on explicitly absent frames.",
    "media": {
      "type": "video",
      "src": "/media/rallyvision-heldout.mp4",
      "poster": "/media/rallyvision-heldout-poster.png",
      "ariaLabel": "Held-out RallyVision table-tennis footage with predicted and annotated ball trajectories"
    }
  },
  {
    "id": "viewshift",
    "chapter": "research",
    "title": "ViewShift",
    "label": "Cross-view temporal action segmentation",
    "period": "2026 · Research system",
    "description": "A leakage-controlled Assembly101 study comparing temporal models on fresh recordings from a camera that is absent from training.",
    "tech": "Python · PyTorch · Assembly101 · MobileNetV3 · BiGRU · TCN",
    "metrics": [
      {
        "value": "11.35",
        "label": "held-out F1@50 gain over TCN"
      },
      {
        "value": "38.40 ± 6.58",
        "label": "final held-out-camera F1@50"
      },
      {
        "value": "3 seeds",
        "label": "frozen confirmatory protocol"
      }
    ],
    "note": "The gain is a segment-quality tradeoff, not a universal robustness claim. Frame accuracy declined by 6.26 points, and the custom subset is not the official Assembly101 benchmark split.",
    "media": [
      {
        "type": "video",
        "src": "/media/viewshift-results.mp4",
        "poster": "/media/viewshift-results-poster.png",
        "ariaLabel": "The same Assembly101 action recorded from two training cameras and the held-out ViewShift camera"
      },
      {
        "type": "image",
        "src": "/media/viewshift-cross-view-results.png",
        "alt": "Bar chart comparing TCN and BiGRU segment metrics on the fresh held-out camera across three seeds"
      },
      {
        "type": "image",
        "src": "/media/viewshift-seed-results.png",
        "alt": "Line chart comparing held-out-camera F1 at 50 for TCN and BiGRU across seeds 7, 19, and 31"
      }
    ]
  },
  {
    "id": "real-time-procedural-vlm",
    "chapter": "research",
    "title": "Real-Time Procedural VLM",
    "label": "Streaming vision-language systems",
    "period": "2026 · Research prototype",
    "description": "A streaming assistant that converts sparse egocentric frames into narrow visual observations while a deterministic local tracker owns procedure state, duplicate suppression, and recovery.",
    "tech": "Python · vision-language models · async inference · deterministic tracking · replay evaluation",
    "metrics": [
      {
        "value": "2 FPS",
        "label": "sparse visual sampling"
      },
      {
        "value": "1.59 s",
        "label": "measured mean API-call latency"
      },
      {
        "value": "3.14 s",
        "label": "average p95 API-call latency"
      }
    ],
    "note": "Live output stability remains an explicit release gate. Replayable observation logs and deterministic state are the core result, not a claim of solved procedural understanding.",
    "media": {
      "type": "video",
      "src": "/media/vlm-procedural-demo.mp4",
      "poster": "/media/vlm-procedural-demo-poster.jpg",
      "ariaLabel": "Egocentric repair footage streamed through the real-time procedural VLM"
    }
  },
  {
    "id": "robot-viewpoint",
    "chapter": "research",
    "title": "Robot Viewpoint Robustness",
    "label": "Vision-only manipulation",
    "period": "2026 · In progress",
    "description": "A controlled Robosuite study of how camera shift changes a vision-only behavior-cloning policy, with fixed-camera and camera-randomized training evaluated under the same rollout harness.",
    "tech": "Robosuite · MuJoCo · behavior cloning · controlled camera perturbations",
    "metrics": [
      {
        "value": "RGB only",
        "label": "policy observation"
      },
      {
        "value": "0–40°",
        "label": "controlled viewpoint sweep"
      },
      {
        "value": "Panda",
        "label": "PickPlaceCan platform"
      }
    ],
    "note": "The public page shows the evaluation apparatus and rollout visualizations. It does not present the illustrative website curve as measured policy telemetry.",
    "media": {
      "type": "video",
      "src": "/media/robot-viewpoint-rollouts.mp4",
      "poster": "/media/robot-viewpoint-rollouts-poster.jpg",
      "ariaLabel": "Panda arm manipulation rollouts from multiple controlled camera viewpoints"
    }
  },
  {
    "id": "vla-manip",
    "chapter": "research",
    "title": "VLA-Manip",
    "label": "Vision-language-action adaptation",
    "period": "2026 · Research prototype",
    "description": "A manipulation research scaffold for adapting OpenVLA to LIBERO tasks with parameter-efficient supervised training, followed by a separately evaluated verifiable-reward refinement stage.",
    "tech": "PyTorch · OpenVLA · LIBERO · LoRA · verifiable rewards",
    "metrics": [
      {
        "value": "7B",
        "label": "OpenVLA base model"
      },
      {
        "value": "LIBERO",
        "label": "manipulation benchmark"
      },
      {
        "value": "LoRA → RL",
        "label": "staged adaptation plan"
      }
    ],
    "note": "This is presented as an active research prototype. No unfinished policy-improvement result is claimed as completed.",
    "media": {
      "type": "video",
      "src": "/media/vla-manip-libero.mp4",
      "poster": "/media/vla-manip-libero-poster.jpg",
      "ariaLabel": "LIBERO manipulation tasks with a Panda arm across kitchen and tabletop scenes"
    }
  },
  {
    "id": "ai-racing-lane-perception",
    "chapter": "applied",
    "title": "AI Racing Tech / Lane Perception",
    "label": "Autonomous racing",
    "period": "2023–2024 · UC Berkeley",
    "description": "Contributions to the Indy Autonomous Challenge stack spanning vehicle-dynamics integration, perception and controller simulation, and automated validation before track testing.",
    "tech": "ROS 2 · autonomous racing · simulation · Pure Pursuit · model-based control · Azure CI",
    "metrics": [
      {
        "value": "ROS 2",
        "label": "autonomy integration"
      },
      {
        "value": "Indy",
        "label": "autonomous race platform"
      },
      {
        "value": "CI",
        "label": "repeatable stack validation"
      }
    ],
    "note": "This was team-based autonomy work. The portfolio description is limited to the systems and validation work I directly contributed.",
    "media": {
      "type": "video",
      "src": "/media/airt-telemetry.mp4",
      "poster": "/media/airt-telemetry-poster.jpg",
      "ariaLabel": "AI Racing Tech trackside telemetry and autonomy dashboards"
    }
  },
  {
    "id": "lexius-video-intelligence",
    "chapter": "applied",
    "title": "Lexius / Video Intelligence",
    "label": "Production CV/ML engineering",
    "period": "2024 · Internship",
    "description": "Production video-classification work across dataset quality, evaluation instrumentation, cloud migration, and model-serving services for retail camera intelligence.",
    "tech": "computer vision · video classification · AWS · GCP · model serving",
    "metrics": [
      {
        "value": "+5%",
        "label": "classification accuracy"
      },
      {
        "value": "+19%",
        "label": "F1 score"
      },
      {
        "value": "GCP → AWS",
        "label": "serving migration"
      }
    ],
    "note": "Metrics are from production-like validation work completed during the internship. Customer-specific implementation details remain private.",
    "media": [
      {
        "type": "video",
        "src": "/media/lexius-alerts.mp4",
        "poster": "/media/lexius-alerts-poster.jpg",
        "ariaLabel": "Lexius real-time retail video alerts demo"
      },
      {
        "type": "video",
        "src": "/media/lexius-traceback.mp4",
        "poster": "/media/lexius-traceback-poster.jpg",
        "ariaLabel": "Lexius video traceback following a person through a grocery store"
      }
    ]
  }
];

export default cvProjects;
